import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1 cursor-pointer rounded-full border font-semibold w-fit transition-colors duration-200',
  {
    variants: {
      variant: {
        default: 'border-border-default bg-background text-weak hover:bg-weak/20',

        neutral: 'text-neutral bg-neutral/10 hover:bg-neutral/20 border-neutral',
        brown: 'text-brown bg-brown/10 hover:bg-brown/20 border-brown',
        citrine: 'text-citrine bg-citrine/10 hover:bg-citrine/20 border-citrine',
        amber: 'text-amber bg-amber/10 hover:bg-amber/20 border-amber',
        orange: 'text-orange bg-orange/10 hover:bg-orange/20 border-orange',
        lime: 'text-lime bg-lime/10 hover:bg-lime/20 border-lime',
        green: 'text-green bg-green/10 hover:bg-green/20 border-green',
        grass: 'text-grass bg-grass/10 hover:bg-grass/20 border-grass',
        sky: 'text-sky bg-sky/10 hover:bg-sky/20 border-sky',
        emerald: 'text-emerald bg-emerald/10 hover:bg-emerald/20 border-emerald',
        blue: 'text-blue bg-blue/10 hover:bg-blue/20 border-blue',
        purple: 'text-purple bg-purple/10 hover:bg-purple/20 border-purple',
        fuchsia: 'text-fuchsia bg-fuchsia/10 hover:bg-fuchsia/20 border-fuchsia',
        red: 'text-red bg-red/10 hover:bg-red/20 border-red',
        magenta: 'text-magenta bg-magenta/10 hover:bg-magenta/20 border-magenta',

        success: 'text-success bg-success/10 hover:bg-success/20 border-success',
        warning: 'text-warning bg-warning/10 hover:bg-warning/20 border-warning',
        critical: 'text-critical bg-critical/10 hover:bg-critical/20 border-critical',
      },
      size: {
        small: 'min-h-4.5 px-1.5 text-[0.625rem]',
        large: 'min-h-7 px-2 text-[0.75rem]',
      },
      outlined: {
        false: 'border-transparent',
      },
      clickable: {
        false: 'pointer-events-none',
      },
      icon: {
        true: 'justify-center self-center px-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      clickable: false,
      outlined: false,
      size: 'small',
      icon: false,
    },
    compoundVariants: [
      { icon: true, size: `small`, class: `w-4.5` },
      { icon: true, size: `large`, class: `w-7` },
    ],
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export type BadgeColor = BadgeProps['variant'];

function Badge({ className, variant, size, outlined, icon, clickable, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size, outlined, icon, clickable }), className)}
      {...props}
    />
  );
}

export { badgeVariants, Badge };
